// React
import React, { useRef, useState, useEffect, Fragment } from 'react'
import PropTypes from 'prop-types'

// Libraries
import { useFormikContext } from 'formik'

// Components
import BCartPicker from 'ui/blocks/Picker/types/cart'
import BPicker from 'ui/blocks/Picker'
import Icon from 'ui/components/Icon'
import { DurationsPicker } from './Picker'
import * as Drawer from 'ui/components/Picker/Drawer'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'

// Shared
import { useTranslation } from 'shared/utils/useTranslation'
import { Format as FormatUtils } from 'back_office/v2/utils/utils'

export const DurationsMobile = ({ onChange, ...props }) => {
  const timeoutRef = useRef(null)
  const portalRef = useRef(null)

  const [open, setOpen] = useState(false)

  const picker = usePicker()
  const form = useFormikContext()
  const { t } = useTranslation('user')

  const option = props.options?.find((option) => option.value === props.durationObject?.id)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const selectWithTimeout = (selection) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      picker.setSelection(selection)
    }, 750)
  }

  const handleOpen = (e, type) => {
    e.preventDefault()

    setOpen(true)

    selectWithTimeout(type)
  }

  const handleChange = (value) => {
    onChange(value)

    if (!picker.useTimes && !picker.hasInitialValues) {
      setOpen(false)
    }
  }

  const handleStopTimeChange = () => {
    if (picker.hasInitialValues) return

    setOpen(false)
  }

  const handleDrawerOpenChange = (open) => {
    // Reset selection on drawer close
    if (!open) {
      picker.setSelection(null)
    }

    setOpen(open)
  }

  return (
    <Fragment>
      <BCartPicker.Durations disabled={props.disabled} className="wrap">
        <BPicker.Field>
          <BPicker.Label>{t('user.store.rental_duration')}</BPicker.Label>
          <BPicker.Button onClick={(e) => handleOpen(e, 'duration')} empty={!props.durationObject} data-tid="Durations">
            {option?.label || t('user.store.rental_duration')}
          </BPicker.Button>
        </BPicker.Field>
        <BCartPicker.Period modifiers="width-100p" className={picker.useTimes === false ? 'no-times' : ''} disableStyling>
          <BPicker.Field>
            <BPicker.Label>{t('user.store.start')}</BPicker.Label>
            <BPicker.Button
              onClick={(e) => handleOpen(e, 'from')}
              empty={!form.values.starts_at}
              error={(!!form.errors.starts_at?.date || !!form.errors.starts_at?.time)}
              data-tid="Starts at picker"
            >
              {form.values.starts_at ?
                FormatUtils.date(form.values.starts_at, picker.useTimes ? 'dateTime' : 'date') :
                t(
                  picker.useTimes ?
                    'user.store.select_date_and_time_placeholder' :
                    'user.store.select_date_placeholder'
                )}
            </BPicker.Button>
          </BPicker.Field>
          <BPicker.Decorator>
            <Icon icon="arrow-right" />
          </BPicker.Decorator>
          <BPicker.Field>
            <BPicker.Label>{t('user.store.stop')}</BPicker.Label>
            <BPicker.Button
              onClick={(e) => handleOpen(e, 'to time')}
              empty={!form.values.stops_at}
              disabled={props.isReturnTimeDisabled || !props.durationObject}
              error={(!!form.errors.stops_at?.date || !!form.errors.stops_at?.time)}
              data-tid="Stops at picker"
            >
              {form.values.stops_at ?
                FormatUtils.date(form.values.stops_at, picker.useTimes ? 'dateTime' : 'date') :
                t(
                  picker.useTimes ?
                    'user.store.select_date_and_time_placeholder' :
                    'user.store.select_date_placeholder'
                )}
            </BPicker.Button>
          </BPicker.Field>
        </BCartPicker.Period>
      </BCartPicker.Durations>
      <Drawer.Root open={open} onOpenChange={handleDrawerOpenChange}>
        <Drawer.Header>
          <Drawer.Title>{t('user.store.select_a_period')}</Drawer.Title>
          <DurationsPicker
            {...props}
            numberOfMonths={1}
            portalRef={portalRef}
            onChange={handleChange}
            onStopTimeChange={handleStopTimeChange}
            maxHeight="100%"
            width="100%"
            isMobile
            usePortal
          />
        </Drawer.Header>
        <Drawer.Body>
          <BCartPicker.Portal ref={portalRef} />
        </Drawer.Body>
      </Drawer.Root>
    </Fragment>
  )
}

DurationsMobile.propTypes = {
  isReturnTimeDisabled: PropTypes.bool,
  options: PropTypes.array,
  durationObject: PropTypes.object,
  disabled: PropTypes.bool,
  onMonthChange: PropTypes.func,
  onChange: PropTypes.func
}
